import React from 'react';
import { View, Text, Button, FlatList, ScrollView } from 'react-native';
import { useWebSocketMeasurementService } from '@shared/hooks/useWebSocketMeasurementService';
import { formatMeasurementValue as formatMeasurementValueUtil } from '@shared/utils/webSocketUtils';
import { dynamicTranslations } from '@shared/utils/dynamicTranslation';
import { alertTypeResult, extractMeasurementValues, IMeasurementData, formatMeasurementValue } from 'app/_modules/core/utils';
import { ActivityName } from 'app/_modules/core/types';

function WsMessagesScreenContent() {
  const { status, measurements, connect, disconnect } = useWebSocketMeasurementService();
  const { t } = dynamicTranslations();

  return (
    <View className="flex-1 p-4 gap-3">
      <Text className="text-xl font-bold">Real-time Measurements</Text>
      <Text className="opacity-70">Status: {status}</Text>

      <View className="flex-row items-center">
        <Button title="Reconnect" onPress={connect} />
        <View className="w-3" />
        <Button title="Disconnect" onPress={disconnect} />
      </View>
      <FlatList
        className="flex-1 mt-2"
        data={measurements}
        keyExtractor={(item) => item.id}
        renderItem={({ item }) => {
          const activityName = item.activityName as ActivityName;
          let alertType: 'red' | 'yellow' | 'green' = 'green';
          let thresholdType: 'low' | 'high' | 'high_low' | 'boolean' | 'weight_variation_percentage' | undefined;
          if (item.alert?.alertDetails && item.alert.alertDetails.length > 0) {
            const alertDetail = item.alert.alertDetails[0];
            alertType = alertDetail.alertType;
            thresholdType = alertDetail.thresholdType;
          }
          let measurement: Record<string, any>;
          if (typeof item.value === 'object' && item.value !== null) {
            measurement = item.value as Record<string, any>;
          } else {
            measurement = { [item.type]: item.value };
          }

          const measurementData: IMeasurementData = {
            type: activityName,
            measurement,
            timestamp: item.measuredAt,
          };

          const measurementValues = extractMeasurementValues(measurementData);
          const { color, title, description, type } = alertTypeResult(activityName, alertType, thresholdType);
          const { value: primaryValue, unit: displayUnit, showUnit } = formatMeasurementValue(activityName, measurementValues, t);

          let valueFontSize = 'text-5xl';
          if (activityName === ActivityName.BLOOD_PRESSURE) {
            valueFontSize = 'text-2xl';
          } else if (activityName === ActivityName.ECG) {
            valueFontSize = 'text-3xl';
          }

          return (
            <View className="p-4 rounded-xl my-2 bg-white border-2" style={{ borderColor: color.bg.includes('error') ? '#EF4444' : color.bg.includes('warning') ? '#F59E0B' : '#10B981' }}>
              <View className="flex-row justify-between items-center mb-3">
                <Text className="text-sm font-semibold uppercase text-gray-600">{item.type}</Text>
                <Text className="text-xs text-gray-500">
                  {new Date(item.measuredAt).toLocaleTimeString()}
                </Text>
              </View>
              <View className="items-center mb-4">
                <View 
                  className={`items-center justify-center h-32 w-32 rounded-full border-4 ${color.bg}`}
                >
                  <View className="items-center justify-center">
                    <Text className={`${valueFontSize} font-bold leading-none ${color.text}`}>
                      {primaryValue}
                    </Text>
                    {showUnit && (
                      <Text className={`text-sm leading-none -mt-1 ${color.text}`}>
                        {displayUnit}
                      </Text>
                    )}
                  </View>
                </View>
              </View>

              <View className="items-center mb-3">
                <Text className="text-lg font-semibold text-center">
                  {t(type)} {t(title)}
                </Text>
                <Text className="text-sm text-gray-600 text-center mt-1">
                  {t(description)}
                </Text>
              </View>

              <View className="mt-3 pt-3 border-t border-gray-200">
                <Text className="text-xs text-gray-500 mb-1">
                  🔍 Debug: alert_type={alertType}, threshold_type={thresholdType || 'N/A'}
                </Text>
                <Text className="text-xs text-gray-500">
                  Device: {item.deviceModel || item.deviceId} | Status: {item.status}
                </Text>
              </View>
            </View>
          );
        }}
        inverted
      />
    </View>
  );
}

export default function WsMessagesScreen() {
  return <WsMessagesScreenContent />;
}


